import React, { FunctionComponent, useState } from 'react';
import moment from 'moment';
import indefinite from 'indefinite';
import { Form, Inspection, License } from '../../../../store/modules/application/types';
import FormDisplay from '../../../../components/FormDisplay';
import Loader from '../../../../components/Loader';
import { getRequiredScopes, useAuthenticationContext } from 'src/authentication';
import { Api } from 'src/store/services/services';
import { siteActions } from 'src/store/actions';
import Modal from 'src/components/Modal';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

type InspectionDataProps = {
    editForm: Form;
    inspectionDetails: Inspection;
    updateInspection: Function;
    isLoading: Boolean;
    setError: Function;
};

const InspectionData: FunctionComponent<InspectionDataProps> = (props) => {
    const { inspectionDetails, editForm, isLoading, updateInspection, setError } = props;
    const { license } = inspectionDetails;

    const [selectedInspector, setSelectedInspector] = useState<License>();
    const [inspectionInfo, setInspectionInfo] = useState<Inspection>();
    const [isConfirmationModalOpen, setModal] = useState(false);

    const handleSelectedInspector = (selectedInspectorDetails: License) => {
        setSelectedInspector(selectedInspectorDetails);
    };

    const authContext = useAuthenticationContext();
    const { scopes } = authContext;
    const getAccessToken = authContext.getAccessToken;

    const errorMessage = (field: string) => {
        setError({
            isError: true,
            errorCode: 400,
            message: `${field} is required`,
        });
    };
    let inspector: any;
    const handleSave = async (updatedInspection: Inspection) => {
        let makeUpdateInspectionCall = true;
        const { inspectionDate } = updatedInspection;
        updatedInspection.inspectionDate = inspectionDate ? moment(inspectionDate).format('YYYY-MM-DD') : null;
        if (updatedInspection?.inspector?.license) {
            inspector = { license: { id: updatedInspection?.inspector?.license.id || '' } };
        } else if (updatedInspection?.inspector?.id?.includes('search->')) {
            inspector = { license: { id: updatedInspection?.inspector?.id.replace('search->', '') } };
            updatedInspection.inspector = inspector;
            setInspectionInfo(updatedInspection);

            // If the selected inspector is 3rd party inspector, then check the category configurations
            let url: string = '/licenseTypes';

            const scope = getRequiredScopes(url);
            const workflowToken = await getAccessToken({ scopes: [scopes['workflows.read']] }).then(
                (response) => response.accessToken,
            );
            if (scope && workflowToken) {
                try {
                    const token = await authContext
                        .getAccessToken({ scopes: [scopes[scope]] })
                        .then((response) => response.accessToken);
                    const licenseType = await Api.getLicenseType(token, selectedInspector?.type?.id);
                    const workflowDetails = await Api.getWorkflowById(workflowToken, licenseType.workflowId);
                    // Check for category status configured for 3rd party inspector
                    const isCategoryFieldActive: boolean = workflowDetails.states.some((workFlowState) => {
                        if (workFlowState.category) {
                            if (
                                selectedInspector?.status?.toLowerCase() === workFlowState.category.toLowerCase() &&
                                workFlowState.category.toLowerCase() === 'active'
                            ) {
                                return true;
                            }
                        }
                        return false;
                    });

                    //If category field is not configured or status is 'active'
                    //check the selected inspector status
                    if (!isCategoryFieldActive) {
                        //If the status is other than active show a warning modal
                        if (selectedInspector?.status.toLowerCase() !== 'active') {
                            setModal(true);
                            makeUpdateInspectionCall = false;
                        }
                    }
                } catch (err) {
                    siteActions.setError(err);
                }
            }
        } else {
            inspector = { id: updatedInspection?.inspector?.id || '' };
        }

        if (!updatedInspection?.status) {
            errorMessage('Status ');
        } else if (makeUpdateInspectionCall) {
            updatedInspection.inspector = inspector;
            updateInspection(updatedInspection, inspectionDetails.id);
        }
    };

    const onClose = () => {
        setModal(false);
    };
    const onConfirm = () => {
        setModal(false);
        if (!inspectionInfo?.status) {
            errorMessage('Status ');
        } else {
            updateInspection(inspectionInfo, inspectionDetails.id);
        }
    };

    if (isLoading) return <Loader />;

    const content = (
        <div>
            You have selected an inspector with{' '}
            <strong>{indefinite(!!selectedInspector ? selectedInspector?.status?.toLowerCase() : '')}</strong>{' '}
            <strong>{selectedInspector?.type.name}</strong>. Are you sure you want to update this inspection?
        </div>
    );

    return (
        <>
            <FormDisplay
                form={editForm}
                record={{ ...inspectionDetails, ...{ _context: { license } } }}
                handleSave={handleSave}
                handleSelectedInspector={handleSelectedInspector}
            />
            <Modal open={isConfirmationModalOpen} maxWidth={'sm'} fullWidth={false} minHeight={'10vh'}>
                <ConfirmationDialog
                    onClose={onClose}
                    onDismiss={onConfirm}
                    buttonContent="Proceed"
                    cancelButtonContent="BACK"
                    contentTitle="Confirm Inspection"
                    content={content}
                />
            </Modal>
        </>
    );
};

export default InspectionData;
